import { submitServerChange } from 'components/dataCreationForms/common';
import { OpenCreateDialogParams } from 'components/dataCreationForms/types';
import {
  makeHandleFormValidation,
  validate,
} from 'components/dataCreationForms/validation';
import { getContractsStoreState, useContracts } from 'store/contract';
import { useSites } from 'store/site';
import {
  contractCreateOnViewToContractCreateModel,
  createEmptyContract,
} from 'shared/domain/contract/mapping';
import { contractClient } from 'shared/domain/contract/contractClient';
import { ContractCreateOnView } from 'shared/domain/contract/types/view';
import { keepUndeleted } from 'shared/domain/deletable/filters';
import { toLabelledEntities } from 'helpers/misc';
import { useProjectId } from 'store/project';
import { ErrorPresentation } from 'helpers/validators';
import { useCancelConfirmation } from 'presentation/dialogForms/dialogFormsHooks';
import {
  ComponentType,
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { CreateContractContext } from '../types';
import { getCreateContractValidationSchema } from '../validation';
import { useAppNavigation } from 'contexts/navigation';

const formName = 'contract';
const SUBMIT_EVENT_TYPE = 'submit-contract';
const WithCreateContractContext = createContext<
  CreateContractContext | undefined
>(undefined);

function WithCreateContract({
  children,
  onSuccessCallback,
}: {
  children: ReactElement;
  onSuccessCallback?: (_id: string, label: string) => void;
}): ReactElement {
  const { goToContractDetails } = useAppNavigation();
  const {
    items: contracts,
  } = useContracts();
  const {
    items: sites,
    loading: loadingSites,
  } = useSites();
  const [isPosting, setIsPosting] = useState<boolean>(false);
  const availableSites = useMemo(
    () => toLabelledEntities(sites.filter(keepUndeleted)),
    [sites]
  );
  const releaseSubmitEvent = useCallback(() => {
    window.dispatchEvent(new CustomEvent(SUBMIT_EVENT_TYPE));
  }, []);

  const projectId = useProjectId();

  const emptyContract = createEmptyContract();
  const [initialValues, setInitialValues] =
    useState<ContractCreateOnView>(emptyContract);
  const validationSchema = useMemo(() => {
    return getCreateContractValidationSchema(contracts, null);
  }, [contracts]);

  const validateCreateContract = useCallback(
    (
      key: keyof ContractCreateOnView,
      value: ContractCreateOnView[keyof ContractCreateOnView]
    ): ErrorPresentation => {
      return validate(key, value, validationSchema);
    },
    [validationSchema]
  );

  const handleFormValidation = useMemo(() => {
    return makeHandleFormValidation(validationSchema);
  }, [validationSchema]);
  const [open, setOpen] = useState(false);
  const openDialog = useCallback(
    ({ freeSoloLabel }: OpenCreateDialogParams) => {
      setOpen(true);
      initialValues &&
        freeSoloLabel &&
        setInitialValues({ ...initialValues, label: freeSoloLabel });
    },
    [initialValues]
  );
  const onSuccess = useCallback(
    (_id, label) => onSuccessCallback?.(_id, label),
    [onSuccessCallback]
  );

  const confirmCancel = useCancelConfirmation(formName);
  const openConfirmationDialog = useCallback(async () => {
    const { cancelConfirmed } = await confirmCancel();
    if (!cancelConfirmed) {
      return;
    }
    setOpen(false);
  }, [confirmCancel]);

  const submitForm = useCallback(
    async (values: ContractCreateOnView) => {
      const contractCreateModel = contractCreateOnViewToContractCreateModel(values, projectId);
      const outcome = await submitServerChange({
        change: () => contractClient.create(contractCreateModel),
        setOpen,
        setIsPosting,
        action: 'creating a contract',
      });
      if (!outcome.saved) {
        return;
      }

      const contract = outcome.result;
      // The details view reads the contract from the store.
      await getContractsStoreState().actions.reload();
      onSuccess(contract._id, contract.label);
      if (window.location.pathname.includes('/contract/create')) {
        goToContractDetails(contract._id);
      }
    },
    [goToContractDetails, onSuccess, projectId]
  );

  const ctx: CreateContractContext = useMemo(() => {
    return {
      open,
      openDialog,
      closeDialog: openConfirmationDialog,
      submitForm,
      isPosting,
      initialValues,
      SUBMIT_EVENT_TYPE,
      releaseSubmitEvent,
      validate: validateCreateContract,
      handleFormValidation,
      availableSites,
      onSuccess,
      formName,
    };
  }, [
    openConfirmationDialog,
    initialValues,
    isPosting,
    open,
    openDialog,
    releaseSubmitEvent,
    submitForm,
    availableSites,
    onSuccess,
    handleFormValidation,
    validateCreateContract,
  ]);

  return (
    <WithCreateContractContext.Provider value={ctx}>
      {children}
    </WithCreateContractContext.Provider>
  );
}

function useCreateContract(): CreateContractContext {
  const context = useContext(WithCreateContractContext);
  if (context === undefined) {
    throw new Error(
      'useCreateContract must be used within an CreateContractContext'
    );
  }
  return context;
}

const withCreateContract =
  (Component: ComponentType<any>) =>
  ({ ...props }): ReactElement => (
    <WithCreateContract>
      <Component {...props} />
    </WithCreateContract>
  );

export { WithCreateContract, useCreateContract, withCreateContract };
